import React, { useState, useEffect } from 'react';
import { PublicIP, PublicIPCreate, PublicIPUpdate, publicIpApi, Office } from '../services/api';

interface PublicIPFormProps {
  selectedOffice: Office | null;
  publicIP: PublicIP | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const PublicIPForm: React.FC<PublicIPFormProps> = ({ selectedOffice, publicIP, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    cidr: '',
    isp: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (publicIP) {
      setFormData({
        cidr: publicIP.cidr,
        isp: publicIP.isp,
        description: publicIP.description || ''
      });
    } else {
      setFormData({
        cidr: '',
        isp: '',
        description: ''
      });
    }
  }, [publicIP]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedOffice && !publicIP) {
      setError('Please select an office');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (publicIP) {
        const updateData: PublicIPUpdate = {
          cidr: formData.cidr,
          isp: formData.isp,
          description: formData.description || undefined
        };
        await publicIpApi.update(publicIP.id, updateData);
      } else {
        const createData: PublicIPCreate = {
          office_id: selectedOffice!.id,
          cidr: formData.cidr,
          isp: formData.isp,
          description: formData.description || undefined
        };
        await publicIpApi.create(createData);
      }
      onSuccess();
      setFormData({
        cidr: '',
        isp: '',
        description: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save public IP');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h2>{publicIP ? 'Edit Public IP' : 'Add Public IP'}</h2>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>CIDR (e.g., 203.0.113.0/24):</label>
          <input
            type="text"
            value={formData.cidr}
            onChange={(e) => setFormData({ ...formData, cidr: e.target.value })}
            required
            placeholder="203.0.113.0/24"
          />
        </div>
        <div className="form-group">
          <label>ISP:</label>
          <input
            type="text"
            value={formData.isp}
            onChange={(e) => setFormData({ ...formData, isp: e.target.value })}
            required
            placeholder="KT, SK Broadband, LG U+, etc."
          />
        </div>
        <div className="form-group">
          <label>Description:</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Main line, Backup line, etc."
          />
        </div>
        <div className="button-group">
          <button type="submit" className="btn btn-primary" disabled={loading || !selectedOffice}>
            {loading ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PublicIPForm;